import TripModule from '../TripModule';
import { TrainFilters, type TrainFiltersData } from './TrainModuleTypes';
import { TaxiLateralLoader } from '@itinerary/trip/bus/TaxiLateralLoader';
import type Train from '@itinerary/trip/train/Train';
import type TrainPackage from '@itinerary/trip/train/TrainPackage';
import type TrainPriceClass from '@itinerary/trip/train/TrainPriceClass';
import {
    type Point,
    TimeFilter,
    TripSortBy,
} from '@itinerary/trip/TripModuleTypes';
import {
    ButtonGroupFilter,
    CheckboxFilter,
    type EmptyListReason,
    type Filter,
    PriceFilter,
} from '@itinerary/ItineraryModuleTypes';
import {
    SellTypeCode,
    type TrainLocation,
    type TrainUrlParams,
} from '@utils/train';
import { TripType } from '@utils/trip';
import { type TrainPackageLoaderConfig } from '@itinerary/trip/train/types';
import TrainPackageSet from '@itinerary/trip/train/TrainPackageSet';
import {
    GetAvailableStatus,
    type MidStationResponse,
    TrainApiApiMidStationTrainCapacityIdGet,
} from '@clients/TrainApi';
import { TrackingType } from '@itinerary/types';

export default class TrainModule extends TripModule<
    Train,
    TrainPriceClass,
    TrainFiltersData,
    TrainLocation,
    TrainPackage,
    TrainPackageSet
> {
    override descriptionTemplate =
        'خرید اینترنتی بلیط قطار (مبدا) (مقصد). بلیط قطار سریع‌السیر و کوپه‌ای و برنامه حرکت قطارها. ابتدا وارد وب‌سایت شوید. قطارها را جستجو سپس بلیط قطار (مبدا) (مقصد) را به صورت آنلاین رزرو کنید. مِستربلیط سایت و اپلیکیشن رزرو اینترنتی بلیط قطار با ارزان‌ترین قیمت و بدون نیاز به ثبت نام';

    override get defaultSortBy(): TripSortBy {
        return TripSortBy.Time;
    }

    override get reserveInfo() {
        return useTrainForm().reserveInfo;
    }

    override getPackageSet(
        loaderConfig: TrainPackageLoaderConfig
    ): TrainPackageSet {
        return new TrainPackageSet(loaderConfig, this.getInitialFilters());
    }

    override applyFilter(
        pkg: TrainPackage,
        filters: { [key in TrainFiltersData['key']]: any }
    ): boolean {
        if (!pkg.segments.length) {
            return false;
        }

        const [train] = pkg.segments[0].trips;
        let price: TrainPriceClass | null | undefined =
            pkg.defaultPrice as TrainPriceClass;
        if (!train) {
            return false;
        }
        if (!price) {
            price = pkg.selectedPrices?.[0] as TrainPriceClass;
        }
        if (!price) {
            return false;
        }
        const departureMinutes = timeToMinutes(train.departureTime);
        const arrivalMinutes =
            train.arrivalTime && timeToMinutes(train.arrivalTime);
        return (
            (!filters.corporation ||
                filters.corporation.length === 0 ||
                filters.corporation.some((a: any) =>
                    pkg.segments.some(s =>
                        s.trips.some(f =>
                            `,${a},`.includes(`,${f.corporationInfo.code},`)
                        )
                    )
                )) &&
            (!filters.price ||
                (pkg.singlePayable >= filters.price[0] &&
                    pkg.singlePayable <= filters.price[1])) &&
            (!filters.time ||
                !filters.time.departure ||
                filters.time.departure.length === 0 ||
                (departureMinutes <= 8 * 60 &&
                    filters.time.departure.includes('midnight')) ||
                (departureMinutes >= 8 * 60 &&
                    departureMinutes <= 18 * 60 &&
                    filters.time.departure.includes('day')) ||
                (departureMinutes >= 18 * 60 &&
                    filters.time.departure.includes('night'))) &&
            (!filters.time ||
                !filters.time.arrival ||
                filters.time.arrival.length === 0 ||
                (+arrivalMinutes <= 8 * 60 &&
                    filters.time.arrival.includes('midnight')) ||
                (+arrivalMinutes >= 8 * 60 &&
                    +arrivalMinutes <= 18 * 60 &&
                    filters.time.arrival.includes('day')) ||
                (+arrivalMinutes >= 18 * 60 &&
                    filters.time.arrival.includes('night'))) &&
            (!filters.wagonType ||
                filters.wagonType.length === 0 ||
                (!price.isCompartment && filters.wagonType.includes('bus')) ||
                (price.isCompartment &&
                    price.compartmentCapacity === 4 &&
                    filters.wagonType.includes('4p')) ||
                (price.isCompartment &&
                    price.compartmentCapacity === 6 &&
                    filters.wagonType.includes('6p'))) &&
            (!filters.hotel ||
                filters.hotel.length === 0 ||
                (price.hasHotel && filters.hotel.includes('true')) ||
                (!price.hasHotel && filters.hotel.includes('false')))
        );
    }

    override get lateralLoader(): TaxiLateralLoader | null {
        return new TaxiLateralLoader();
    }

    override get emptyListReason(): EmptyListReason {
        if (this.hasPreSell) {
            return {
                image: '/img/no-result/pre-sell.png',
                title: '',
                notFoundText:
                    'خرید بلیط قطار برای تاریخ انتخابی شما در پیش فروش بعدی راه آهن امکان‌پذیر است!',
                idOfElement: 'train-presell',
            };
        }
        return super.emptyListReason;
    }

    get hasPreSell() {
        return this.currentPackageSet?.metaData?.hasPreSell ?? false;
    }

    override get supportedTrackingTypes() {
        const trainForm = useTrainForm();
        const superTracks = super.supportedTrackingTypes.filter(
            val => val !== TrackingType.Price
        );
        if (trainForm.sellType.code === SellTypeCode.Car) {
            return [];
        } else if (this.hasPreSell) {
            return [...superTracks, TrackingType.Presell];
        } else {
            return superTracks;
        }
    }

    override get originLocation(): Point | null {
        const trainForm = useTrainForm();
        if (super.originLocation) {
            return super.originLocation;
        }
        const origin = trainForm.trainStations.find(
            station => station.code.toString() === trainForm.originCode
        );
        if (origin?.latitude && origin.longitude) {
            return origin as Point;
        }
        return null;
    }

    override get destinationLocation(): Point | null {
        const trainForm = useTrainForm();
        if (super.destinationLocation) {
            return super.destinationLocation;
        }
        const destination = trainForm.trainStations.find(
            station => station.code.toString() === trainForm.destinationCode
        );
        if (destination?.latitude && destination.longitude) {
            return destination as Point;
        }
        return null;
    }

    override get filters(): Filter[] {
        if (!this.currentPackageSet?.filterMetaData) {
            return [];
        }
        const trainForm = useTrainForm();

        const filterData = this.currentPackageSet.filterMetaData;
        return this.sortFilters([
            new PriceFilter(
                TrainFilters.price,
                filterData.minPrice ?? 0,
                filterData.maxPrice ?? 0
            ),
            new TimeFilter(
                TrainFilters.time,
                trainForm.origin?.title ?? '',
                trainForm.destination?.title ?? ''
            ),
            new ButtonGroupFilter(
                TrainFilters.wagonType,
                [
                    {
                        id: '4p',
                        title: '۴ نفره',
                    },
                    {
                        id: '6p',
                        title: '۶ نفره',
                    },
                    {
                        id: 'bus',
                        title: 'سالنی',
                    },
                ],
                'نوع کوپه'
            ),
            new ButtonGroupFilter(
                TrainFilters.hotel,
                [
                    {
                        id: 'true',
                        title: 'با هتل',
                    },
                    {
                        id: 'false',
                        title: 'بدون هتل',
                    },
                ],
                'هتل'
            ),
            new CheckboxFilter(
                TrainFilters.corporation,
                filterData.corporations?.map(c => ({
                    id: c.ids?.toString() ?? '',
                    title: c.name ?? '',
                })) ?? [],
                'شرکت‌های مسافربری'
            ),
        ]);
    }

    override get metaTagTitle(): string {
        return `خرید بلیط ${this.reserveInfo.title} ${this.originName} ${this.destinationName} (برنامه حرکت قطارها) - مِستربلیط`;
    }

    override getItineraryPackageLoaderConfigs(
        params: TrainUrlParams
    ): TrainPackageLoaderConfig[] {
        const trainForm = useTrainForm();
        const ret: TrainPackageLoaderConfig[] = [
            {
                originCode: params.originCode.toString(),
                destinationCode: params.destinationCode.toString(),
                date: params.departureDate,
                adultCount: params.adultCount || 1,
                childCount: params.childCount ?? 0,
                infantCount: params.infantCount ?? 0,
                sellType: params.sellTypeCode || SellTypeCode.Family,
                exclusive: params.exclusive ?? false,
                url: params.url,
                tripType: trainForm.tripType,
                originName: trainForm.origin?.title,
                destinationName: trainForm.destination?.title,
                totalSeatCount: 0, // fake
                isRoundTrip: this.isRoundTrip,
            },
        ];

        if (params.returnDate) {
            const loader: TrainPackageLoaderConfig = {
                originCode: params.destinationCode.toString(),
                destinationCode: params.originCode.toString(),
                date: params.returnDate,
                adultCount: params.adultCount || 1,
                childCount: params.childCount ?? 0,
                infantCount: params.infantCount ?? 0,
                sellType: params.sellTypeCode || SellTypeCode.Family,
                exclusive: params.exclusive ?? false,
                url: params.url,
                tripType: trainForm.tripType,
                originName: trainForm.destination?.title,
                destinationName: trainForm.origin?.title,
                totalSeatCount: 0, // fake
                isRoundTrip: this.isRoundTrip,
            };
            const [, returnItineraryPackageSet] = this.itineraryPackageSets;
            if (
                trainForm.tripType === TripType.RoundTrip &&
                this.isSelectingReturn &&
                returnItineraryPackageSet
            ) {
                loader.availableStatus =
                    returnItineraryPackageSet.availableStatus;
                loader.selectedCapacityId =
                    returnItineraryPackageSet.selectedCapacityId;
            }
            ret.push(loader);
        }

        return ret;
    }

    override beforeSelectPackage(params: {
        package: TrainPackage;
        index: number;
    }) {
        const trainForm = useTrainForm();
        if (
            trainForm.tripType === TripType.RoundTrip &&
            !!this.itineraryPackageSets?.[params.index + 1]
        ) {
            const packageSet = this.itineraryPackageSets[params.index + 1];
            if (params.package.isForRoundTrip && packageSet) {
                packageSet.availableStatus = GetAvailableStatus.OnlyRoundTrip;
                packageSet.selectedCapacityId =
                    params.package.selectedPrice!.id;
            } else if (
                packageSet &&
                packageSet.availableStatus !== GetAvailableStatus.OnlyOneWay
            ) {
                packageSet.availableStatus = GetAvailableStatus.OnlyOneWay;
                packageSet.selectedCapacityId = undefined;
            }
            if (params.package.mergedSegment) {
                this.itineraryPackageSets = this.itineraryPackageSets.slice(
                    0,
                    1
                );
            }
        }
    }

    override getInitialFilters(): { [key in TrainFiltersData['key']]: any } {
        return {
            corporation: null,
            hotel: null,
            price: null,
            time: null,
            wagonType: null,
        };
    }

    private timeSeparator(time: string) {
        return (time ?? '').split(':').map(Number);
    }

    async getPackageMidStations(
        trainCapacityId: number
    ): Promise<MidStationResponse[]> {
        const { data } = await TrainApiApiMidStationTrainCapacityIdGet({
            trainCapacityId,
        });
        return (
            data.toSorted((current, next) => {
                const [hour, minute, second] = this.timeSeparator(
                    current.time ?? ''
                );
                const [nextHour, nextMinute, nextSecond] = this.timeSeparator(
                    next.time ?? ''
                );
                const nextDate = dayConfig
                    .gregory(next.date)
                    .hour(nextHour)
                    .minute(nextMinute)
                    .second(nextSecond);
                return dayConfig
                    .gregory(current.date)
                    .hour(hour)
                    .minute(minute)
                    .second(second)
                    .isBefore(nextDate)
                    ? -1
                    : 1;
            }) || []
        );
    }

    override formAutoReserve(): Promise<void> {
        return Promise.reject(new Error('Not implemented'));
    }
}
